import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom"; // Import Link for navigation
import {
  Lightbulb,
  Briefcase,
  Users,
  MapPin,
  Loader,
  ArrowRight,
  FileText,
  Clock,
  CheckCircle,
  Calendar,
  Trash2,
  Plus,
  ChevronRight,
  AlertCircle,
  Download,
  ExternalLink,
  Leaf,
  ChevronDown,
  ArrowLeft,
  Copy,
  EyeIcon,
  BadgeCheck,
  RefreshCw,
  BarChart4,
  Link2,
  Sparkles,
  BookOpen,
  Star,
  LucideBriefcaseBusiness,
  Rocket,
  ArrowUpRight,
  SlidersHorizontal,
  ChevronsUpDown,
} from "lucide-react"; // Import all required icons
import axiosInstance from "./axiosConfig"; // Assuming this configuration exists
import EnhancedHeader from "./EnhancedHeader"; // Assuming this component exists

// Define the BusinessDashboard component
const BusinessDashboard = () => {
  // --- Hooks ---
  const navigate = useNavigate();
  const copyTimeoutRef = useRef(null);
  const formRef = useRef(null);

  // --- State Variables ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [formError, setFormError] = useState("");
  const [listError, setListError] = useState("");
  const [reports, setReports] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    business_idea: "",
    business_stage: "",
    team_info: "", // Team info is mandatory
    location: "",
  });

  // --- Data Fetching ---
  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setListError("");
    try {
      const response = await axiosInstance.get("dynamicreports/");
      setReports(response.data);
    } catch (err) {
      console.error("Error fetching reports:", err.response || err);
      setListError("Could not load your existing plans. Please refresh.");
    } finally {
      setReportsLoading(false);
    }
  }, []);

  // --- Effects ---
  useEffect(() => {
    fetchReports();
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, [fetchReports]);

  // --- Event Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Updated to include team_info in clearing error
    if (formError && (name === "title" || name === "business_idea" || name === "team_info")) {
      setFormError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Validation includes team_info
    if (!formData.title.trim() || !formData.business_idea.trim() || !formData.team_info.trim()) {
      let missingFields = [];
      if (!formData.title.trim()) missingFields.push("Title");
      if (!formData.business_idea.trim()) missingFields.push("Business Idea");
      if (!formData.team_info.trim()) missingFields.push("Team Information");
      setFormError(`Please provide: ${missingFields.join(", ")}.`);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post("reports/", formData);
      navigate(`/dynamicreports/${response.data.id}`);
    } catch (err) {
      console.error("Error creating report:", err);
      const apiError =
        err.response?.data?.error || err.response?.data?.detail || "Plan creation failed. Please review your input or try again.";
      setFormError(apiError);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this plan permanently? This cannot be undone.")) return;
    setDeleteLoading(id);
    setListError("");
    try {
      await axiosInstance.delete(`reports/${id}/`);
      setReports((prev) => prev.filter((report) => report.id !== id));
      if (expandedReport === id) setExpandedReport(null);
    } catch (err) {
      console.error("Error deleting report:", err);
      setListError("Could not delete the plan. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleToggleExpand = useCallback((id, e) => {
    e?.stopPropagation();
    setExpandedReport((current) => (current === id ? null : id));
  }, []);

  const handleCopy = useCallback((text, id, e) => {
    e?.stopPropagation();
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopySuccess(id);
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = setTimeout(() => setCopySuccess(null), 2500);
      })
      .catch((err) => {
        console.error("Copy failed: ", err);
        setListError("Could not copy text.");
      });
  }, []);

  const handleOpenReport = (id) => {
    navigate(`/dynamicreports/${id}`);
  };

  // --- Utility Functions ---
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  // --- Inline Render Functions ---

  const renderSkeletonCard = (key) => (
    <div
      key={key}
      className="bg-white rounded-lg shadow-sm border border-l-4 border-transparent border-gray-200/70 p-5 animate-pulse space-y-4"
    >
      <div className="flex justify-between items-center">
        {" "}
        <div className="h-3.5 bg-gray-200 rounded w-1/3"></div> <div className="h-5 w-5 bg-gray-200 rounded-full"></div>{" "}
      </div>
      <div className="h-5 bg-gray-300 rounded w-3/4"></div>
      <div className="space-y-1.5">
        {" "}
        <div className="h-2.5 bg-gray-200 rounded w-full"></div> <div className="h-2.5 bg-gray-200 rounded w-5/6"></div>{" "}
      </div>
      <div className="h-2.5 bg-gray-200 rounded w-1/2"></div>
      <div className="flex justify-between items-center border-t border-gray-100 pt-4 mt-4">
        {" "}
        <div className="flex space-x-2">
          {" "}
          <div className="h-7 w-7 bg-gray-200 rounded-md"></div> <div className="h-7 w-7 bg-gray-200 rounded-md"></div>{" "}
        </div>{" "}
        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>{" "}
      </div>
    </div>
  );

  const renderReportCard = (report) => {
    const isExpanded = expandedReport === report.id;
    const isCopySuccess = copySuccess === report.id;
    const isDeleteLoading = deleteLoading === report.id;
    const isSample = report.is_global;

    return (
      <div
        key={report.id}
        className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200/80 border-l-4 border-green-500 flex flex-col overflow-hidden transform hover:-translate-y-1"
      >
        <div className="px-5 pt-4 pb-2 flex justify-between items-start">
          {/* Show either Sample badge OR Preview/Full Access badge (not both) */}
          {isSample ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-green-100 text-green-800 ring-1 ring-inset ring-green-200">
              <Star className="h-3 w-3 mr-1" /> Sample
            </span>
          ) : (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
                report.is_purchased
                  ? "bg-green-100 text-green-800 ring-1 ring-inset ring-green-200"
                  : "bg-yellow-100 text-yellow-800 ring-1 ring-inset ring-yellow-200"
              }`}
            >
              {report.is_purchased ? "Full Access" : "Preview"}
            </span>
          )}

          {report.is_completed && (
            <div className="flex items-center text-xs text-teal-600 font-medium mt-0.5">
              <BadgeCheck className="h-4 w-4 mr-1 opacity-80" /> <span>Ready</span>
            </div>
          )}
        </div>

        <div className="px-5 pt-2 pb-5 flex-grow cursor-pointer" onClick={() => handleOpenReport(report.id)}>
          <h3
            className="text-lg font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-200 mb-3 line-clamp-2"
            title={report.title}
          >
            {report.title || "Untitled Plan"}
          </h3>

          <div className="relative mb-4">
            <p
              className={`text-sm text-gray-600 leading-relaxed transition-all duration-300 ease-in-out ${
                isExpanded ? "line-clamp-none" : "line-clamp-3"
              }`}
            >
              {report.business_idea || "No description provided."}
            </p>
            {!isExpanded && report.business_idea && report.business_idea.length > 120 && (
              <div className="absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none"></div>
            )}
          </div>

          <div className="space-y-2 text-xs text-gray-500 font-medium">
            {report.business_stage && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" /> <span>{report.business_stage} Stage</span>
              </div>
            )}
            {report.location && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="truncate" title={report.location}>
                  {report.location}
                </span>
              </div>
            )}
            <div className="flex items-center">
              <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" /> <span>{formatDate(report.created_at)}</span>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50/80 border-t border-gray-100/80 flex justify-between items-center">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <button
              onClick={(e) => handleToggleExpand(report.id, e)}
              className="p-2 text-gray-500 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
              title={isExpanded ? "Show Less" : "Show More"}
            >
              <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>

            <button
              onClick={(e) => handleCopy(report.business_idea, report.id, e)}
              disabled={!report.business_idea}
              className={`relative p-2 rounded-md transition-colors duration-200 flex items-center ${
                isCopySuccess
                  ? "text-green-600 bg-green-50"
                  : "text-gray-500 hover:text-green-700 hover:bg-green-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500"
              }`}
              title={report.business_idea ? "Copy Idea" : "No idea to copy"}
            >
              <Copy className={`transition-opacity duration-200 h-4 w-4 sm:h-5 sm:w-5 ${isCopySuccess ? "opacity-0" : "opacity-100"}`} />
              {isCopySuccess && <CheckCircle className="absolute inset-0 m-auto h-4 w-4 sm:h-5 sm:w-5 text-green-600" />}
            </button>

            {report.pdf && (
              <a
                href={report.pdf}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 text-gray-500 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                title="Download PDF"
              >
                <Download className="h-4 w-4 sm:h-5 sm:w-5" />
              </a>
            )}

            {!isSample && (
              <button
                onClick={(e) => handleDelete(report.id, e)}
                disabled={isDeleteLoading}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200 disabled:opacity-40"
                title="Delete Report"
              >
                {isDeleteLoading ? (
                  <Loader className="h-4 w-4 sm:h-5 sm:w-5 animate-spin text-red-500" />
                ) : (
                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            )}
          </div>

          <button
            onClick={() => handleOpenReport(report.id)}
            className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
            title="Open Report"
          >
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>
    );
  };
  const renderEmptyListState = () => (
    <div className="text-center py-16 px-6 bg-white rounded-lg shadow-sm border border-gray-200/60 max-w-2xl mx-auto">
      <Rocket className="h-12 w-12 text-green-400 mx-auto mb-5 opacity-80" />
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Ready to Launch Your Plan?</h3>
      <p className="text-gray-500 text-sm max-w-md mx-auto">
        Your generated business plans will appear here. Use the form above to create your first one using our AI architect.
      </p>
    </div>
  );

  // --- Main Component Render ---
  return (
    <div className="min-h-screen bg-gray-100">
      <EnhancedHeader /> {/* Assumed Header Component */}
      <main className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8">
        {/* === TOP SECTION: Intro & Create Form (Centered) === */}
        {/* Using max-w-4xl and mx-auto for centering */}
        <section className="max-w-4xl mx-auto mb-16 sm:mb-20" ref={formRef}>
          {/* ** Reverted Intro Text and Styling ** */}
          <div className="mb-10 lg:mb-12">
            {" "}
            {/* Removed text-center */}
            <div className="inline-flex items-center bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 shadow-sm">
              {" "}
              {/* Badge from previous version */}
              <Sparkles className="h-4 w-4 mr-1.5 opacity-80" />
              Your Personal Business Consultant{/* Text from previous version */}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl mb-4 leading-tight">
              {" "}
              {/* Heading from previous version */}
              Craft Your Unique Business Plan
            </h1>
            {/* ** Using the user-provided text ** */}
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-5">
              Our AI now builds a dynamic <span className="font-semibold text-green-700/90">55+ section business plan </span>. No two
              business plans are the same now. Start below!
            </p>
            {/* ** More prominent Legacy Link styling from previous version ** */}
            <p className="text-md text-gray-700 font-bold">
              {" "}
              {/* Adjusted styling slightly */}
              Looking for the old planner/created reports? Visit the{" "}
              <Link to="/report/" className="text-green-600 hover:text-green-700 underline font-bold">
                [Legacy Planner]
              </Link>
            </p>
          </div>
          {/* ** End Reverted Intro Text and Styling ** */}

          {/* --- Creation Form Panel --- */}
          <div className="bg-white rounded-xl shadow-xl border border-gray-200/70 overflow-hidden p-6 sm:p-8 lg:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {formError && (
                <div className="rounded-md bg-red-50 p-4 border border-red-200">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{formError}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-800 mb-1">
                    Plan Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., EchoRep – AI Meeting Avatars"
                    className="block w-full rounded-md border-gray-300 shadow-sm py-2 px-3 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="location" className="block text-sm font-medium leading-6 text-gray-800 mb-1">
                    Location
                  </label>
                  <div className="relative mt-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="location"
                      id="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="e.g., New York City, NY / Global Expansion"
                      className="block w-full rounded-md border-gray-300 pl-9 py-2 px-3 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-green-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="business_idea" className="block text-sm font-medium leading-6 text-gray-800 mb-1">
                  Your Business Idea <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="business_idea"
                  name="business_idea"
                  rows={5}
                  required
                  value={formData.business_idea}
                  onChange={handleChange}
                  placeholder="Describe your core concept, target audience, key features/services, and unique selling proposition..."
                  className="block w-full rounded-md border-gray-300 shadow-sm py-2 px-3 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-green-500 sm:text-sm leading-relaxed"
                />
                <p className="mt-1.5 text-xs text-gray-500">Provide detail! The AI uses this to personalize every section.</p>
              </div>

              <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="business_stage" className="block text-sm font-medium leading-6 text-gray-800 mb-1">
                    Stage
                  </label>
                  <div className="relative mt-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Briefcase className="h-4 w-4 text-gray-400" />
                    </div>
                    <select
                      id="business_stage"
                      name="business_stage"
                      value={formData.business_stage}
                      onChange={handleChange}
                      className="block w-full appearance-none rounded-md border-gray-300 py-2 pl-9 pr-10 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-green-500 sm:text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="Idea">Idea</option>
                      <option value="Pre-Seed">Pre-Seed</option>
                      <option value="Seed">Seed</option>
                      <option value="Early Growth">Early Growth</option>
                      <option value="Established">Established</option>
                      <option value="Scaling">Scaling</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronsUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                {/* === MODIFIED TEAM INFO FIELD === */}
                <div>
                  <label htmlFor="team_info" className="block text-sm font-medium leading-6 text-gray-800 mb-1">
                    Team Information <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="team_info"
                    name="team_info"
                    rows={4} // Adjusted rows for textarea
                    required // Attribute stays
                    value={formData.team_info}
                    onChange={handleChange}
                    placeholder={
                      // Multi-line placeholder
                      `Arjun Malhotra (CEO) – Former product lead at HelloFresh

Irene Lee (Culinary Anthropologist) – 12 years studying food and culture

Luis Fernandez (CTO) – Built AI for Spotify’s mood-based music curation`
                    }
                    className="block w-full rounded-md border-gray-300 shadow-sm py-2 px-3 placeholder:text-gray-400 placeholder:text-xs placeholder:leading-relaxed focus:ring-1 focus:ring-inset focus:ring-green-500 sm:text-sm leading-relaxed" // Adjusted placeholder style
                  />
                </div>
                {/* === END MODIFIED TEAM INFO FIELD === */}
              </div>

              <div className="pt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex items-center justify-center rounded-lg border border-transparent bg-gradient-to-r from-green-600 to-emerald-600 px-7 py-3 text-base font-semibold text-white shadow-md transition-all duration-300 ease-in-out hover:shadow-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-60 group ${
                    isSubmitting ? "cursor-wait" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="animate-spin -ml-1 mr-3 h-5 w-5" />
                      Generating Your Plan...
                    </>
                  ) : (
                    <>
                      Architect My Plan <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </div>
              <p className="text-center text-xs text-gray-500 pt-2">Plan generation typically takes 2-3 minutes.</p>
            </form>
          </div>
        </section>

        {/* === DIVIDER / Section Header for Reports === */}
        {/* Reverted to less prominent divider title */}
        <div className="relative pt-8 pb-6 md:pt-10 md:pb-8">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300/60"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-gray-100 px-4 text-lg font-medium text-gray-600">Your Generated Plans</span>
          </div>
        </div>

        {/* === BOTTOM SECTION: Previous Plans List (Centered) === */}
        {/* Using max-w-6xl and mx-auto for centering */}
        <section className="max-w-6xl mx-auto">
          {listError && (
            <div className="mb-6 max-w-2xl mx-auto rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{listError}</p>
                </div>
              </div>
            </div>
          )}

          {reportsLoading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {" "}
              {/* Increased gap */}
              {[1, 2, 3].map((i) => renderSkeletonCard(i))}
            </div>
          ) : reports.length === 0 ? (
            renderEmptyListState()
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {" "}
              {/* Increased gap */}
              {reports.map((report) => renderReportCard(report))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default BusinessDashboard;
